import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowRight, 
  CheckCircle, 
  MapPin, 
  DollarSign, 
  MessageSquare, 
  Star,
  Tractor,
  Trees,
  Mountain,
  Shovel
} from 'lucide-react';

const FEATURES = [
  {
    icon: MapPin,
    title: 'Post Your Job',
    description: 'Describe your land clearing needs - acreage, terrain, timeline. Get matched with local contractors.'
  },
  {
    icon: DollarSign,
    title: 'Receive Competitive Bids',
    description: 'Contractors compete for your business. Compare prices, reviews, and equipment capabilities.'
  },
  {
    icon: MessageSquare,
    title: 'Chat & Coordinate',
    description: 'Direct messaging with contractors. Discuss details, share photos, finalize terms.'
  },
  {
    icon: Star,
    title: 'Pay Securely',
    description: 'Secure payments through our platform. Only release funds when the job is complete.'
  }
];

const SERVICES = [
  { icon: Trees, name: 'Land Clearing', description: 'Remove trees, brush, and vegetation' },
  { icon: Tractor, name: 'Mulching', description: 'Forestry mulching and brush clearing' },
  { icon: Mountain, name: 'Excavation', description: 'Earthmoving and site preparation' },
  { icon: Shovel, name: 'Grading', description: 'Land leveling and drainage work' }
];

const STATS = [
  { value: '2,500+', label: 'Jobs Completed' },
  { value: '850+', label: 'Contractors' },
  { value: '4.8', label: 'Avg Rating' },
  { value: '$2.5M+', label: 'Paid to Contractors' }
];

export const LandingPage = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-stone-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-stone-950/90 backdrop-blur-sm border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_clearing-crew/artifacts/qizuc7gv_file_00000000d05471f59cc81c7b9868657c.png" 
                alt="IronLink" 
                className="h-14"
              />
            </Link>
            <div className="flex items-center gap-4">
              {user ? (
                <Link to={user.role === 'contractor' ? '/contractor/dashboard' : '/customer/dashboard'}>
                  <Button 
                    data-testid="dashboard-btn"
                    className="bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wide rounded-sm"
                  >
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/auth">
                    <Button variant="ghost" data-testid="login-btn" className="text-stone-300 hover:text-white">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/auth">
                    <Button 
                      data-testid="get-started-btn"
                      className="bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wide rounded-sm"
                    >
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-16">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1763515186653-2df0fba44697?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxleGNhdmF0b3IlMjBsYW5kJTIwY2xlYXJpbmclMjBoZWF2eSUyMG1hY2hpbmVyeXxlbnwwfHx8fDE3NzA0NDk3MzR8MA&ixlib=rb-4.1.0&q=85"
            alt="Excavator"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-950/80 via-stone-950/70 to-stone-950" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white uppercase leading-tight animate-fadeInUp">
              <span className="text-orange-500">Clear Land.</span>
              <br />
              Build Dreams.
            </h1>
            <p className="mt-6 text-xl text-stone-300 max-w-2xl animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
              Connect with professional land clearing contractors in your area. 
              Post jobs, receive competitive bids, and get your project done right.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
              <Link to="/auth">
                <Button 
                  size="lg"
                  data-testid="hero-post-job-btn"
                  className="h-14 px-8 bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wide text-lg rounded-sm btn-active"
                >
                  Post a Job
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/jobs">
                <Button 
                  size="lg"
                  variant="outline"
                  data-testid="hero-browse-jobs-btn"
                  className="h-14 px-8 border-stone-600 text-white hover:bg-stone-800 font-heading uppercase tracking-wide text-lg rounded-sm"
                >
                  Browse Jobs
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
            {STATS.map((stat, index) => (
              <div key={index} className="text-center md:text-left">
                <div className="font-heading text-4xl lg:text-5xl text-orange-500">{stat.value}</div>
                <div className="text-stone-400 mt-1 uppercase tracking-wide text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl lg:text-5xl text-white uppercase">Services We Cover</h2>
            <p className="mt-4 text-stone-400 text-lg">Find contractors for any land clearing need</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((service, index) => (
              <div 
                key={index}
                className="industrial-card p-8 hover:bg-stone-800/50 transition-colors"
              >
                <service.icon className="w-12 h-12 text-orange-500 mb-4" />
                <h3 className="font-heading text-2xl text-white uppercase">{service.name}</h3>
                <p className="mt-2 text-stone-400">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-stone-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl lg:text-5xl text-white uppercase">How It Works</h2>
            <p className="mt-4 text-stone-400 text-lg">Simple, transparent, efficient</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {FEATURES.map((feature, index) => (
              <div key={index} className="relative">
                <div className="absolute -top-4 -left-2 font-heading text-8xl text-orange-500/20">
                  {index + 1}
                </div>
                <div className="relative z-10 pt-8">
                  <feature.icon className="w-10 h-10 text-orange-500 mb-4" />
                  <h3 className="font-heading text-2xl text-white uppercase">{feature.title}</h3>
                  <p className="mt-3 text-stone-400">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contractor CTA */}
      <section className="py-24 bg-stone-900 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden lg:block">
          <img
            src="https://images.unsplash.com/photo-1763515187478-9934edce7158?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxleGNhdmF0b3IlMjBsYW5kJTIwY2xlYXJpbmclMjBoZWF2eSUyMG1hY2hpbmVyeXxlbnwwfHx8fDE3NzA0NDk3MzR8MA&ixlib=rb-4.1.0&q=85"
            alt="Skid steer"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-xl">
            <h2 className="font-heading text-4xl lg:text-5xl text-white uppercase">
              Are You a <span className="text-orange-500">Contractor?</span>
            </h2>
            <p className="mt-6 text-stone-300 text-lg">
              Join IronLink and grow your business. Access jobs in your area, 
              manage bids, and get paid securely through our platform.
            </p>

            <ul className="mt-8 space-y-4">
              {[
                'Free to browse jobs',
                'Competitive subscription plans',
                'Direct customer communication',
                'Secure payment processing'
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3 text-stone-300">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <div className="mt-10">
              <Link to="/auth">
                <Button 
                  size="lg"
                  data-testid="contractor-signup-btn"
                  className="h-14 px-8 bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wide text-lg rounded-sm"
                >
                  Join as Contractor
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-stone-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl lg:text-5xl text-white uppercase">Contractor Plans</h2>
            <p className="mt-4 text-stone-400 text-lg">Choose the plan that fits your business</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: 'Basic',
                price: 20,
                features: ['Browse jobs', 'Submit up to 5 bids/month', 'Basic profile', 'Email support']
              },
              {
                name: 'Professional',
                price: 30,
                popular: true,
                features: ['Unlimited bids', 'Featured profile', 'Priority support', 'Profile analytics']
              },
              {
                name: 'Premium',
                price: 50,
                features: ['All Pro features', 'Featured in search', 'Priority job alerts', 'Dedicated support']
              }
            ].map((plan, index) => (
              <div 
                key={index}
                className={`industrial-card p-8 relative ${
                  plan.popular ? 'border-orange-500 bg-stone-900' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white px-4 py-1 text-sm font-heading uppercase rounded-sm">
                    Most Popular
                  </div>
                )}
                <h3 className="font-heading text-2xl text-white uppercase">{plan.name}</h3>
                <div className="mt-4 flex items-baseline">
                  <span className="font-heading text-5xl text-orange-500">${plan.price}</span>
                  <span className="text-stone-400 ml-2">/month</span>
                </div>
                <ul className="mt-8 space-y-4">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-stone-300">
                      <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/auth" className="block mt-8">
                  <Button 
                    className={`w-full h-12 font-heading uppercase tracking-wide rounded-sm ${
                      plan.popular 
                        ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                        : 'bg-stone-800 hover:bg-stone-700 text-white border border-stone-700'
                    }`}
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-stone-900 border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_clearing-crew/artifacts/qizuc7gv_file_00000000d05471f59cc81c7b9868657c.png" 
              alt="IronLink" 
              className="h-14"
            />
            <p className="text-stone-500 text-sm">
              © 2024 IronLink. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
